import { useState, useEffect } from 'react'
import supabase from '@/supabaseClient'
import { AppUser } from './types'

export const useUserProfile = () => {
  const [user, setUser] = useState<AppUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isEditingName, setIsEditingName] = useState(false)
  const [editedName, setEditedName] = useState('')

  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const {
          data: { user: currentUser },
          error: userError,
        } = await supabase.auth.getUser()
        if (userError) throw userError
        if (currentUser) {
          setUser(currentUser)
          setEditedName(currentUser.user_metadata?.full_name || '')

          const { data: existingPreferences, error: prefError } = await supabase
            .from('user_preferences')
            .select('id')
            .maybeSingle()

          if (!existingPreferences && !prefError) {
            const { error: insertPrefError } = await supabase
              .from('user_preferences')
              .insert({
                user_id: currentUser.id,
                theme: 'pastel',
                updated_at: new Date().toISOString(),
              })

            if (insertPrefError) {
              console.error(
                'Error inserting default user preferences:',
                insertPrefError
              )
            }
          }
        }
      } catch (error) {
        console.error('Error loading profile:', error)
        setError(
          error instanceof Error ? error.message : 'Failed to load profile'
        )
      } finally {
        setLoading(false)
      }
    }

    loadUserProfile()
  }, [])

  const updateUserName = async () => {
    if (!user) return { success: false, error: 'No user found' }

    try {
      const { error } = await supabase.auth.updateUser({
        data: { full_name: editedName },
      })
      if (error) throw error

      // Update local user state
      setUser({
        ...user,
        user_metadata: {
          ...user.user_metadata,
          full_name: editedName,
        },
      })

      // Exit edit mode
      setIsEditingName(false)
      return { success: true }
    } catch (error) {
      console.error('Error updating name:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      return { success: false, error: errorMessage }
    }
  }

  const cancelNameEdit = () => {
    setIsEditingName(false)
    setEditedName(user?.user_metadata?.full_name || '')
  }

  const startNameEdit = () => {
    setEditedName(user?.user_metadata?.full_name || '')
    setIsEditingName(true)
  }

  return {
    user,
    setUser,
    loading,
    error,
    setError,
    isEditingName,
    editedName,
    setEditedName,
    updateUserName,
    cancelNameEdit,
    startNameEdit,
  }
}